import {
    PAY_CYCLE_BIWEEKLY,
    PAY_CYCLE_MONTHLY,
    PAY_CYCLE_WEEKLY,
} from '@perpay-web/fintech/constants/payCycle';
import {
    dateFormat,
    deserveAddressLineOne,
    deserveAddressLineTwo,
    deserveCity,
    deserveState,
    email,
    emailOrPhone,
    estimatedNetPay,
    exactLength,
    isCardEligibleWithSSIFilter,
    isNotGigEconomyCompany,
    isValidCompany,
    locationIsNotEligible,
    minAgeValidation,
    minDateOfBirth,
    minPaycheckAmount,
    nameNoSpecialCharacters,
    poBoxAllowedAddressLineOne,
    poBoxAllowedAddressLineTwo,
    requiredNonSpace,
    ssiAcknowledgementRequired,
    ssn,
    validateMicroDepositAmount,
    zipCode,
} from '@perpay-web/fintech/validators/fieldBased';
import { phoneNumber, required } from '@perpay-web/validators/fieldBased';
import { passwordSchema, validateForm } from '@perpay-web/validators/formBased';

export const confirmField =
    (fieldName, confirmFieldName, message) => (values) => {
        if (values[fieldName] !== values[confirmFieldName]) {
            return { [confirmFieldName]: [message] };
        }

        return {};
    };

export const monthlyEstimatedNetPay = (values) => {
    const schema = {
        estimatedNetPay: [required, estimatedNetPay],
        payCycle: [required],
    };
    const errors = validateForm(values, schema);

    let minMonthlyEstimatedNetPay = null;
    let duration = null;

    // monthly estimated net pay must be greater than $30 to avoid issues assigning spending limits
    switch (values.payCycle) {
        case PAY_CYCLE_WEEKLY:
            // 6.9 = ($30 * 12 months) / 52 weeks
            if (values.estimatedNetPay <= 6) {
                minMonthlyEstimatedNetPay = 6;
                duration = 'week';
            }

            break;
        case PAY_CYCLE_BIWEEKLY:
            // 13.8 = ($30 * 12 months) / 26 weeks
            if (values.estimatedNetPay <= 13) {
                minMonthlyEstimatedNetPay = 13;
                duration = 'two weeks';
            }

            break;
        case PAY_CYCLE_MONTHLY:
            if (values.estimatedNetPay <= 30) {
                minMonthlyEstimatedNetPay = 30;
                duration = 'month';
            }

            break;
        default:
            // default of bi-weekly
            if (values.estimatedNetPay <= 13) {
                minMonthlyEstimatedNetPay = 13;
                duration = 'two weeks';
            }
    }

    if (minMonthlyEstimatedNetPay && duration) {
        errors.estimatedNetPay = [
            `You must enter an amount higher than $${minMonthlyEstimatedNetPay} per ${duration} in order to be assigned a Spending Limit on Perpay.`,
        ];
    }

    return errors;
};

export const loginFormSchema = {
    email_or_phone: [emailOrPhone, required],
    password: [required],
};

export const emailOrPhoneSchema = {
    email_or_phone: [required, emailOrPhone],
};

export const mfaFormSchema = {
    code: [required, exactLength(6, 'digits')],
};

const nameSchema = {
    firstName: [required, requiredNonSpace, nameNoSpecialCharacters],
    lastName: [required, requiredNonSpace, nameNoSpecialCharacters],
};

export const signupFormSchema = {
    ...passwordSchema,
    ...nameSchema,
    email: [required, email],
};

export const paychexSignUpFormSchema = {
    ...passwordSchema,
    ...nameSchema,
    email: [required, email],
    mobilePhone: [required, phoneNumber],
};

const PAYCHEX_MIN_PAYCHECK_AMOUNT = 30;

export const newUserPaychexConfirmEmployerSchema = {
    companyName: [required, requiredNonSpace],
    paycheckAmount: [
        required,
        minPaycheckAmount(PAYCHEX_MIN_PAYCHECK_AMOUNT),
        estimatedNetPay,
    ],
};

export const existingUserPaychexConfirmEmployerSchema = {
    companyName: [required, requiredNonSpace],
};

export const editEmailFormSchema = {
    email: [required, email],
};

export const microDepositSchema = {
    amount1: [required, validateMicroDepositAmount],
    amount2: [required, validateMicroDepositAmount],
};

export const setPhoneNumberFormSchema = {
    mobilePhone: [required, phoneNumber],
};

export const personalInformationFormSchema = {
    ssn: [required, ssn],
    dateOfBirth: [required, dateFormat, minDateOfBirth],
};

export const identityVerificationFormSchema = {
    ...nameSchema,
    ssn: [required, ssn],
};

export const bankWithMicroDepositSchema = {
    uuid: [required],
    ...microDepositSchema,
};
export const addBankSchema = {
    fullName: [required],
    account: [required],
    routing: [required, exactLength(9, 'digits')],
};

export const addressSchema = {
    street1: [required, deserveAddressLineOne],
    street2: [deserveAddressLineTwo],
    city: [required, deserveCity],
    state: [required, deserveState],
    zipcode: [required, zipCode],
};

export const addressSchemaWithPOBox = {
    street1: [required, poBoxAllowedAddressLineOne],
    street2: [poBoxAllowedAddressLineTwo],
    city: [required, deserveCity],
    state: [required, deserveState],
    zipcode: [required, zipCode],
};

export const cardPersonalDetailsSchema = {
    ...addressSchema,
    ...nameSchema,
    email: [required, email],
    phoneNumber: [required, phoneNumber],
};

export const cardPersonalDetailsExistingUserSchema = {
    ...addressSchema,
    ...nameSchema,
};

export const cardEmploymentDetailsSchema = {
    dateOfBirth: [required, dateFormat, minDateOfBirth, minAgeValidation],
    ssn: [required, ssn],
    payCycle: [required],
    estimatedNetPay: [required, estimatedNetPay],
    selectedCompany: [required, isValidCompany, isCardEligibleWithSSIFilter],
};

export const cardEmploymentDetailsPrefilledSchema = {
    payCycle: [required],
    estimatedNetPay: [required, estimatedNetPay],
    selectedCompany: [required, isValidCompany, isCardEligibleWithSSIFilter],
};

export const marketplaceCardEmploymentDetailsSchema = {
    dateOfBirth: [required, dateFormat, minDateOfBirth, minAgeValidation],
    ssn: [required, ssn],
    payCycle: [required],
    estimatedNetPay: [required, estimatedNetPay],
    selectedCompany: [required, isValidCompany, isNotGigEconomyCompany],
};

export const marketplaceCardEmploymentDetailsPrefilledSchema = {
    payCycle: [required],
    estimatedNetPay: [required, estimatedNetPay],
    selectedCompany: [required, isValidCompany, isNotGigEconomyCompany],
};

export const numberOfPaymentsScheme = {
    numberOfPayments: [required],
};

export const perpayPlusSoloApplicationSchema = {
    ...addressSchema,
    ...personalInformationFormSchema,
    state: [required, locationIsNotEligible],
};

export const ssiAcknowledgementSchema = {
    ssiAcknowledgement: [ssiAcknowledgementRequired],
};

export const addBankAccountSchema = {
    accountNumber: [required],
    routingNumber: [required, exactLength(9, 'digits')],
    // bankName is optional
};
